import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const TIMES = ["09:00 AM", "10:30 AM", "12:00 PM", "02:30 PM", "04:00 PM", "05:30 PM"];

export default function BookingModal({ open, onOpenChange, program }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [intention, setIntention] = useState("");
  const [notes, setNotes] = useState("");
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("form"); setDate(undefined); setTime(""); setIntention(""); setNotes("");
    }
  }, [open]);

  const submit = async () => {
    if (!user) {
      toast.info("Sign in first to hold your session.");
      onOpenChange(false); nav("/login"); return;
    }
    if (!date || !time) { toast.error("Choose a date and time."); return; }
    setBusy(true);
    try {
      await api.post("/bookings", {
        program_id: program?.id || null,
        program_title: program?.title || "Free Discovery Session",
        session_type: program ? "program" : "consultation",
        date: format(date, "yyyy-MM-dd"),
        time, intention, notes,
      });
      setStep("success");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={HOME.bookingModal}
        className="max-w-xl border-none rounded-3xl p-0 overflow-hidden"
        style={{ background: "var(--ev-bg)" }}
      >
        <div className="p-7 md:p-9">
          {step === "form" ? (
            <>
              <DialogHeader className="text-left">
                <div className="eyebrow">{program ? "Program booking" : "Free discovery session"}</div>
                <DialogTitle className="font-serif-ev text-3xl md:text-4xl leading-tight tracking-tight mt-2">
                  {program?.title || "A quiet hour, on us."}
                </DialogTitle>
                <DialogDescription style={{ color: "var(--ev-muted)" }}>
                  Choose a date and time. Prriyanka reviews every request personally.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={HOME.bookingDate}
                              className="mt-2 w-full inline-flex items-center justify-between border hairline rounded-full px-4 py-3 text-sm bg-white">
                        {date ? format(date, "EEE, d MMM yyyy") : "Select a date"}
                        <CalendarIcon className="w-4 h-4 opacity-70" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 bg-white border hairline rounded-2xl" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate}
                                disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>Time</label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger data-testid={HOME.bookingTime} className="mt-2 rounded-full bg-white border hairline">
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-5">
                <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>What are you tending to?</label>
                <input data-testid={HOME.bookingIntention} value={intention}
                       onChange={(e) => setIntention(e.target.value)}
                       placeholder="A phrase is enough."
                       className="mt-2 w-full bg-white border hairline rounded-full py-3 px-4 focus:outline-none focus:border-[color:var(--ev-blue-2)] text-[15px]" />
              </div>
              <div className="mt-5">
                <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>Anything else (optional)</label>
                <textarea data-testid={HOME.bookingNotes} rows={3} value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          className="mt-2 w-full bg-white border hairline rounded-2xl py-3 px-4 focus:outline-none focus:border-[color:var(--ev-blue-2)] text-[15px]" />
              </div>

              <div className="mt-7 flex items-center justify-between gap-3">
                <div className="text-xs" style={{ color: "var(--ev-muted)" }}>
                  {user ? `Booking as ${user.name || user.email}` : "You'll be asked to sign in."}
                </div>
                <button data-testid={HOME.bookingSubmit} onClick={submit} disabled={busy}
                        className="ev-btn ev-btn-primary disabled:opacity-60">
                  {busy ? "Sending…" : "Confirm booking"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="py-4">
              <div className="w-14 h-14 grid place-items-center rounded-full mb-5" style={{ background: "var(--ev-blue-2)" }}>
                <Check className="w-6 h-6 text-white" />
              </div>
              <div className="eyebrow">Confirmed</div>
              <h3 className="font-serif-ev text-3xl md:text-4xl mt-2 leading-tight">Your session is held.</h3>
              <p className="mt-3" style={{ color: "var(--ev-muted)" }}>
                {program?.title || "Free discovery session"} · {date && format(date, "EEE, d MMM yyyy")} · {time}
              </p>
              <p className="mt-2 text-sm" style={{ color: "var(--ev-muted)" }}>Details are in your portal.</p>
              <div className="mt-7 flex gap-3">
                <button onClick={() => onOpenChange(false)} className="ev-btn ev-btn-outline">Close</button>
                <button onClick={() => { onOpenChange(false); nav("/dashboard"); }} className="ev-btn ev-btn-primary">
                  Go to Portal <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
